import { useMemo } from 'react';
import useSWR from 'swr';
import { stringify } from 'query-string';
import { useSelector } from 'react-redux';
import { RootState } from '../../../state/store';
import { configureUrlWithParams } from '../../../helpers/utils';
import type {
  OverviewResponse,
  PerformanceResponse,
  PromotionEngineTab,
  ReportQuery,
  ReportResponse
} from '../types';

const usePromotionEngineFilters = () => {
  const { company } = useSelector((state: RootState) => state);
  const filters = useSelector((state: RootState) => state.filters);

  return useMemo(() => {
    const locationIds = (filters.locations || []) as number[];
    const dates = filters.dates || ['', ''];
    const timeRange = filters.timeRange || [
      [0, 0],
      [0, 0]
    ];

    if (!locationIds.length || !dates[0] || !dates[1]) return null;

    return {
      locations: locationIds,
      dates,
      timeRange,
      widgets: [],
      shouldUseEpocForTimeRange: company.shouldUseEpocForTimeRange,
      timezoneOffset: company?.company_details?.time_zone_offset
    };
  }, [
    filters.locations,
    filters.dates,
    filters.timeRange,
    company.shouldUseEpocForTimeRange,
    company?.company_details?.time_zone_offset
  ]);
};

export const usePromotionEngineOverview = () => {
  const filterParams = usePromotionEngineFilters();
  const endpoint = useMemo(() => {
    if (!filterParams) return null;
    return configureUrlWithParams('/api/v1/promotion-engine/overview', filterParams);
  }, [filterParams]);

  return useSWR<OverviewResponse>(endpoint, {
    revalidateOnFocus: false
  });
};

export const usePromotionEnginePerformance = (enabled: boolean) => {
  const filterParams = usePromotionEngineFilters();
  const endpoint = useMemo(() => {
    if (!enabled || !filterParams) return null;
    return configureUrlWithParams('/api/v1/promotion-engine/performance', filterParams);
  }, [enabled, filterParams]);

  return useSWR<PerformanceResponse>(endpoint, {
    revalidateOnFocus: false
  });
};

export const usePromotionEngineReport = (enabled: boolean, query?: Partial<ReportQuery>) => {
  const filterParams = usePromotionEngineFilters();
  const endpoint = useMemo(() => {
    if (!enabled || !filterParams) return null;
    const base = configureUrlWithParams('/api/v1/promotion-engine/report', filterParams);
    const extra = stringify(
      {
        pagination: query?.pagination || '1:25',
        sort: query?.sort || 'promotion_name',
        sort_dir: query?.sort_dir || 'asc',
        q: query?.q || undefined,
        promotion_ids: query?.promotion_ids?.length ? query.promotion_ids : undefined,
        report_location_ids: query?.report_location_ids?.length
          ? query.report_location_ids
          : undefined
      },
      { skipNull: true, skipEmptyString: true, arrayFormat: 'comma' }
    );
    if (!extra) return base;
    return `${base}${base.includes('?') ? '&' : '?'}${extra}`;
  }, [
    enabled,
    filterParams,
    query?.pagination,
    query?.sort,
    query?.sort_dir,
    query?.q,
    query?.promotion_ids,
    query?.report_location_ids
  ]);

  return useSWR<ReportResponse>(endpoint, {
    revalidateOnFocus: false,
    keepPreviousData: true
  });
};

export type { PromotionEngineTab };
